import { Controller } from '@hotwired/stimulus'
import Editor from '@drenso-toast-ui/editor'
import '@drenso-toast-ui/editor/dist/toastui-editor.css'
import '@drenso-toast-ui/editor/dist/toastui-editor-viewer.css'
import '../stylesheets/toast-editor.css'

// Object reference token embedded in SOP content, e.g. @[gid://shipped-base/Form/1|Returns Intake].
// Rendered as a chip in the editor (see reference_chip_controller for name hydration + broken-ref styling).
const REFERENCE_TOKEN = /@\[(gid:\/\/[^\]|]+)(?:\|([^\]]*))?\]/
// Tolerates Toast UI's markdown escaping of [ ] | (e.g. @\[gid://…\|name\]) so a saved token still matches.
const REFERENCE_TOKEN_RAW_G = /@\\?\[(gid:\/\/[^\]|\\]+)(?:\\?\|((?:\\.|[^\]\\])*))?\\?\]/g

export default class extends Controller {
  static values = {
    height: Number,
    functionChips: { type: Boolean, default: false },
    referenceChips: { type: Boolean, default: false }
  }

  connect() {
    this.boundHandleToggle = this.handleToggleDisabled.bind(this)
    this.element.addEventListener('toggle:disabled', this.boundHandleToggle)
    // Fired by content_reference_autocomplete right after it inserts a token, so the chip renders
    // immediately (same conversion the editor runs on load) instead of only after a page refresh.
    this.boundConvertReferences = () => {
      if (this.referenceChipsValue && this.editor) this.convertInitialReferenceChips()
    }
    this.element.addEventListener('markdown-editor:convert-references', this.boundConvertReferences)
    this.setup()
  }

  handleToggleDisabled(event) {
    const { disabled } = event.detail
    this.teardown()
    if (!disabled) this.setup()
  }

  async setup() {
    this.widgetRules = []
    if (this.functionChipsValue) await this.setupFunctionChips()
    if (this.referenceChipsValue) this.setupReferenceChips()
    this.initializeEditor()
  }

  async setupFunctionChips() {
    try {
      const { fetchBotFunctions } = await import('../lib/bot_functions_cache')
      const functions = await fetchBotFunctions()

      if (functions && functions.length > 0) {
        this.functionMap = new Map(functions.map(f => [f.key, f]))

        // Sort longest-first so longer keys match before shorter prefixes
        const sortedKeys = functions.map(f => f.key).sort((a, b) => b.length - a.length)
        const escapedKeys = sortedKeys.map(k => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))

        // Use word boundaries on both sides so the match captures ONLY the
        // function key — no trailing delimiter absorbed into the widget text.
        // This is safe because we replace Toast UI's InputRule handler with
        // our own (patchInputRules) that handles delimiter insertion separately.
        const pattern = new RegExp(`\\b(${escapedKeys.join('|')})\\b`)

        this.widgetRules.push({
          rule: pattern,
          toDOM: (text) => {
            // Extract the function key from the text passed by widgetToDOM.
            const fullMatch = text.match(pattern)
            let functionKey = fullMatch ? fullMatch[1] : null

            // Fallback: direct lookup or substring search (longest-first)
            if (!functionKey && this.functionMap.has(text)) functionKey = text
            if (!functionKey) {
              for (const key of sortedKeys) {
                if (text.includes(key)) { functionKey = key; break }
              }
            }

            if (!functionKey || !this.functionMap.has(functionKey)) {
              return document.createTextNode(text)
            }

            const func = this.functionMap.get(functionKey)
            const chip = document.createElement('span')
            chip.className = 'function-chip'
            chip.dataset.functionKey = functionKey
            chip.textContent = functionKey

            if (func && !func.available) {
              chip.classList.add('function-chip--unavailable')
            }

            return chip
          }
        })
      }
    } catch (error) {
      console.error('Failed to load function chips:', error)
    }
  }

  // Render @[gid|name] tokens as reference chips. The name snapshot in the token is shown immediately;
  // reference_chip_controller hydrates the current name and marks broken references after render.
  setupReferenceChips() {
    this.widgetRules.push({
      rule: REFERENCE_TOKEN,
      toDOM: (text) => {
        const match = this.unescapeReference(text).match(REFERENCE_TOKEN)
        if (!match) return document.createTextNode(text)

        const chip = document.createElement('span')
        chip.className = 'reference-chip'
        chip.dataset.gid = match[1]
        chip.dataset.name = match[2] || ''
        chip.textContent = match[2] || match[1]
        return chip
      }
    })
  }

  // Toast UI's getMarkdown() escapes markdown punctuation ([ ] |), which corrupts an @[gid|name] token.
  unescapeReference(text) {
    return text.replace(/\\([[\]|_*~`])/g, '$1')
  }

  initializeEditor() {
    const editorHeight = this.hasHeightValue ? `${this.heightValue}px` : '500px'

    // Remove any leftover editor container from Turbo cache restoration
    const stale = this.element.nextElementSibling
    if (stale && stale !== this.editorContainer && stale.querySelector('.toastui-editor-contents')) {
      stale.remove()
    }

    const editorContainer = document.createElement('div')
    editorContainer.style.width = '100%'
    if (!this.element.disabled) {
      editorContainer.style.minHeight = editorHeight
    } else {
      editorContainer.style.padding = '0 0.75rem'
    }
    this.element.insertAdjacentElement('afterend', editorContainer)
    this.editorContainer = editorContainer
    this.element.style.display = 'none'

    const initialValue = this.stripWidgetSyntax(this.element.value)

    const config = {
      el: editorContainer,
      initialValue,
      // Toast UI defaults autofocus to true, which focuses the editor on load
      // and scrolls it into view — jarring when an editor sits mid-page (e.g.
      // the agent "Backstory" field). Keep the page where the user left it.
      autofocus: false,
      width: '100%',
      minHeight: editorHeight,
      initialEditType: 'wysiwyg',
      hideModeSwitch: true,
      toolbarItems: [
        ['heading', 'bold', 'italic'],
        ['hr', 'quote'],
        ['ul', 'ol'],
        ['table', 'link'],
        ['code', 'codeblock']
      ]
    }

    if (this.widgetRules?.length) {
      config.widgetRules = this.widgetRules
    }

    if (this.element.disabled) {
      import('@drenso-toast-ui/editor/dist/toastui-editor-viewer').then(({ default: Viewer }) => {
        this.editor = new Viewer(config)
        this.element.editorInstance = this.editor
        this.element.functionMap = this.functionMap
      })
    } else {
      this.editor = new Editor(config)
      this.element.editorInstance = this.editor
      this.element.functionMap = this.functionMap

      if (this.functionMap) {
        this.patchInputRules()
        // Toast UI widget rules don't apply to initial content in WYSIWYG
        // mode (known limitation). Convert function key text nodes to widget
        // nodes via a ProseMirror transaction after initialization.
        this.convertInitialChips()
      }

      // Same known limitation for reference tokens — convert @[gid|name] text to widget nodes on load.
      if (this.referenceChipsValue) this.convertInitialReferenceChips()

      // Register change handler AFTER initial chip conversion to avoid
      // triggering autosave from the chip-insertion transaction.
      this.editor.on('change', () => {
        const markdown = this.deescapeFunctionKeys(
          this.deescapeLiquid(this.stripWidgetSyntax(this.editor.getMarkdown()).replace(/<br\s*\/?>/gi, '\n'))
        )
        this.element.value = markdown
        this.element.dispatchEvent(new Event('input', { bubbles: true }))
        this.element.dispatchEvent(new Event('change', { bubbles: true }))
        this.element.dispatchEvent(new CustomEvent('markdown-changed', {
          bubbles: true,
          detail: { value: markdown }
        }))
      })
    }
  }

  // Convert function keys in the WYSIWYG ProseMirror document into proper
  // widget nodes so they render as chips on initial load. Handles both
  // widget-wrapped text from md→ww conversion and bare function keys
  // (e.g. in list items where widget rules don't auto-apply).
  convertInitialChips() {
    const wwEditor = this.editor.wwEditor || this.editor.getCurrentModeEditor?.()
    if (!wwEditor?.view) return

    const view = wwEditor.view
    const { schema } = view.state
    const widgetType = schema.nodes.widget
    if (!widgetType) return

    const keys = [...this.functionMap.keys()].sort((a, b) => b.length - a.length)
    const escaped = keys.map(k => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    const joined = escaped.join('|')
    // Match widget-wrapped form OR bare function key (word boundary).
    // The (\W*) capture handles old saved data where trailing delimiters
    // were absorbed into widget text by a previous version of the pattern.
    const combinedPattern = new RegExp(
      `\\$\\$widget\\d+\\s+(${joined})(\\W*)\\$\\$|\\b(${joined})\\b`, 'g'
    )

    // Process replacements one at a time, re-scanning the doc after each
    // to avoid position mapping issues across multiple replacements.
    let found = true
    while (found) {
      found = false
      const { doc: currentDoc } = view.state
      currentDoc.descendants((node, pos, parent) => {
        if (found || !node.isText) return
        // Skip text inside widget nodes (their content is $$widget0 key$$)
        if (parent && parent.type.name === 'widget') return
        combinedPattern.lastIndex = 0
        const match = combinedPattern.exec(node.text)
        if (!match) return
        const funcKey = match[1] || match[3]
        if (!funcKey || !this.functionMap.has(funcKey)) return

        const from = pos + match.index
        const to = from + match[0].length
        const trailing = match[2] || ''

        const widgetContent = `$$widget0 ${funcKey}$$`
        const widgetNode = widgetType.create(
          { info: 'widget0' },
          schema.text(widgetContent)
        )
        const nodes = trailing
          ? [widgetNode, schema.text(trailing)]
          : [widgetNode]

        view.dispatch(view.state.tr.replaceWith(from, to, nodes))
        found = true
      })
    }
  }

  // Convert @[gid|name] reference tokens in the WYSIWYG document into widget nodes so they render as
  // reference chips on initial load (references are inserted as widget nodes live, so no input-rule patch
  // is needed — this only handles previously-saved content).
  convertInitialReferenceChips() {
    const wwEditor = this.editor.wwEditor || this.editor.getCurrentModeEditor?.()
    if (!wwEditor?.view) return

    const view = wwEditor.view
    const widgetType = view.state.schema.nodes.widget
    if (!widgetType) return

    let found = true
    while (found) {
      found = false
      const { doc: currentDoc, schema } = view.state
      currentDoc.descendants((node, pos, parent) => {
        if (found || !node.isText) return
        if (parent && parent.type.name === 'widget') return
        // Match against the RAW text (tolerating Toast UI's escaped [ ] |) so positions stay correct,
        // then reconstruct the clean @[gid|name] token for the widget content.
        REFERENCE_TOKEN_RAW_G.lastIndex = 0
        const match = REFERENCE_TOKEN_RAW_G.exec(node.text)
        if (!match) return

        const from = pos + match.index
        const to = from + match[0].length
        const gid = match[1]
        const name = (match[2] || '').replace(/\\(.)/g, '$1')
        const widgetNode = widgetType.create({ info: 'widget0' }, schema.text(`$$widget0 @[${gid}|${name}]$$`))
        view.dispatch(view.state.tr.replaceWith(from, to, [widgetNode]))
        found = true
      })
    }
  }

  // Replace ProseMirror's InputRules handler so chip conversion only fires
  // on non-word character input, preventing premature conversion when a
  // function key is a prefix of a longer one (e.g. typing "subscriptions_list"
  // shouldn't convert to a chip while the user continues to "_upcoming_orders").
  patchInputRules() {
    const wwEditor = this.editor.wwEditor || this.editor.getCurrentModeEditor?.()
    if (!wwEditor?.view) return

    const view = wwEditor.view
    const plugin = view.state.plugins.find(p => p.spec.isInputRules)
    if (!plugin) return

    const keys = [...this.functionMap.keys()].sort((a, b) => b.length - a.length)
    const escaped = keys.map(k => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    const endPattern = new RegExp(`\\b(${escaped.join('|')})\\W$`)

    plugin.props.handleTextInput = (view, from, to, text) => {
      if (/\w/.test(text)) return false

      const { state } = view
      const $from = state.doc.resolve(from)
      const textBefore = $from.parent.textBetween(
        Math.max(0, $from.parentOffset - 200), $from.parentOffset, null, '\ufffc'
      ) + text

      const match = endPattern.exec(textBefore)
      if (!match) return false

      const funcKey = match[1]
      if (!funcKey || !this.functionMap.has(funcKey)) return false

      const { schema } = state
      const widgetType = schema.nodes.widget
      if (!widgetType) return false

      const widgetContent = `$$widget0 ${funcKey}$$`
      const widgetNode = widgetType.create({ info: 'widget0' }, schema.text(widgetContent))

      // Replace the function key text with the widget, then insert the
      // typed delimiter (space, comma, etc.) after it as a separate text node.
      const keyStart = from - funcKey.length
      const tr = state.tr.replaceWith(keyStart, from, widgetNode)
      const afterWidget = tr.mapping.map(from)
      tr.insertText(text, afterWidget)
      view.dispatch(tr)
      return true
    }
  }

  // Strip Toast UI's internal $$widgetN ...$$ syntax so saved content is clean.
  // Handles both escaped (\$) and unescaped forms from getMarkdown().
  // When functionMap is available, unescape markdown backslashes before lookup
  // to handle Toast UI escaping underscores in text nodes.
  // toastui's getMarkdown() escapes markdown punctuation (\_, \{, \|, \-, ...), which corrupts embedded
  // Liquid: `{% if my_var == "x" %}` round-trips to `{% if my\_var == "x" %}` and the broken tag orphans
  // its `{% elsif %}`/`{% endif %}` ("Unknown tag 'elsif'"). Un-escape inside Liquid tag/object spans
  // (tolerating escaped braces like `\{% … %\}`) so Liquid survives a WYSIWYG round-trip.
  deescapeLiquid(markdown) {
    return markdown.replace(/\\?\{\\?[%{][\s\S]*?[%}]\\?\}/g, (span) => span.replace(/\\(.)/g, '$1'))
  }

  // Toast UI's getMarkdown() escapes underscores in bare text, so a function key that was NOT a live
  // chip at save time (async functionMap not yet loaded, chips disabled, or content imported as plain
  // text) is serialized as `subscriptions\_cancel` and stripWidgetSyntax — which only un-escapes inside
  // $$widget$$ spans — leaves it corrupted. Backend strict-mode matching then can't resolve it. Un-escape
  // the escaped form of every known function key so a save always writes the clean key: this is what lets
  // an already-encoded SOP self-heal on the next edit. Longest-first so a shorter key that prefixes a
  // longer one (subscriptions_list vs subscriptions_list_upcoming_orders) doesn't corrupt the longer form.
  deescapeFunctionKeys(markdown) {
    if (!this.functionMap) return markdown

    let result = markdown
    for (const key of [...this.functionMap.keys()].sort((a, b) => b.length - a.length)) {
      if (!key.includes('_')) continue
      const escaped = key.replace(/_/g, '\\_')
      if (result.includes(escaped)) result = result.split(escaped).join(key)
    }
    return result
  }

  stripWidgetSyntax(markdown) {
    return markdown.replace(/\\?\$\\?\$widget\d+\s(.+?)\\?\$\\?\$/g, (_, content) => {
      // Reference tokens: fully un-escape so the saved @[gid|name] is clean for backend parsing.
      if (/@\\?\[gid:/.test(content)) {
        return content.replace(/\\(.)/g, '$1')
      }
      if (this.functionMap) {
        const unescaped = content.replace(/\\([_*~`])/g, '$1')
        for (const key of [...this.functionMap.keys()].sort((a, b) => b.length - a.length)) {
          if (unescaped.startsWith(key)) {
            return key + unescaped.slice(key.length)
          }
        }
        return unescaped
      }
      return content
    })
  }

  teardown() {
    if (this.editor) {
      this.editor = null
    }
    if (this.editorContainer) {
      this.editorContainer.remove()
      this.editorContainer = null
    }
    this.element.style.display = ''
  }

  disconnect() {
    this.teardown()
    if (this.boundHandleToggle) {
      this.element.removeEventListener('toggle:disabled', this.boundHandleToggle)
    }
    if (this.boundConvertReferences) {
      this.element.removeEventListener('markdown-editor:convert-references', this.boundConvertReferences)
    }
  }
}
